import { matchPath, useLocation, type NavigateFunction } from "react-router-dom";

import Icons from "assets";
import type {
  SidebarMenuItem,
  SidebarMenuSection,
  SidebarWorkspace,
} from "components/partials/sidebar-menu";
import { SidebarMenu } from "components/partials/sidebar-menu";
import { useUnsavedChangesNavigation } from "contexts/unsaved-changes-navigation-context";
import { useCampaign } from "hooks/api/use-campaigns";
import { useClients } from "hooks/api/use-clients";
import type { useCurrentUser } from "hooks/api/use-current-user";
import { Permission } from "types/auth";
import { Route as AppRoute, buildPath, getClientPaths } from "utilities/app-routes";
import { hasPermission } from "utilities/user";
import { useFeatureFlags } from "contexts/feature-flag-context";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import {
  ALL_CLIENTS_WORKSPACE_ID,
  CLIENT_MENU_IDS,
  CLIENT_MENU_ITEM_DEFINITIONS,
  CLIENT_NAV_SECTION_ID,
  getActiveClientMenuItemId,
  getClientSectionFromPathname,
} from "./app-sidebar.constants";

type AppSidebarCurrentUser = ReturnType<typeof useCurrentUser>;

type AdminSidebarContentProps = {
  pathname: string;
  navigate: NavigateFunction;
  currentUser: AppSidebarCurrentUser;
};

export const AdminSidebarContent = ({
  pathname,
  navigate,
  currentUser,
}: AdminSidebarContentProps) => {
  const { requestNavigation } = useUnsavedChangesNavigation();
  const canReadClients = hasPermission(currentUser, Permission.PERM_CLIENT_READ);
  const { data: clientsResponse } = useClients({}, { enabled: canReadClients });
  const featureFlags = useFeatureFlags();

  const matchedClientId = matchPath("/clients/:clientId/*", pathname)?.params?.clientId;
  // Add-client route uses segment "new", not a real client — hide client-specific nav to avoid 404s
  const clientId =
    matchedClientId && matchedClientId !== AppRoute.new ? matchedClientId : undefined;
  const selectedWorkspaceId = clientId || ALL_CLIENTS_WORKSPACE_ID;
  const clientPaths = clientId ? getClientPaths({ clientId }) : undefined;
  const effectiveClientPaths = clientPaths || getClientPaths({ clientId: undefined });

  const clientsForWorkspace = clientsResponse?.items?.length
    ? clientsResponse.items
    : [...currentUser.clients, currentUser.client].filter(Boolean);

  const seenWorkspaceIds: string[] = [];
  const clientWorkspaces: SidebarWorkspace[] = [];

  for (const client of clientsForWorkspace) {
    const id = client.id;
    if (!id || seenWorkspaceIds.includes(id)) {
      continue;
    }

    seenWorkspaceIds.push(id);
    clientWorkspaces.push({ id, name: client.name?.trim() || id });
  }

  const workspaces: SidebarWorkspace[] = [
    { id: ALL_CLIENTS_WORKSPACE_ID, name: "All clients", icon: <Icons.College className="size-4" /> },
    ...clientWorkspaces,
  ];

  const location = useLocation();
  const matchedCampaignId = matchPath("/clients/:clientId/campaigns/:campaignId/*", pathname)?.params
    ?.campaignId;
  // Campaign-new route uses segment "new", not a real campaign — exclude to avoid 404s
  const campaignId =
    matchedCampaignId && matchedCampaignId !== AppRoute.new ? matchedCampaignId : undefined;
  const { data: currentCampaign } = useCampaign(
    {
      clientId,
      id: campaignId,
    },
    {
      enabled: !!clientId && !!campaignId,
    },
  );
  const locationState = location.state as { fromSection?: string; isProgramTemplate?: boolean } | null;
  const isProgramTemplate =
    currentCampaign?.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME ||
    !!locationState?.isProgramTemplate;

  const sections: SidebarMenuSection[] = [
    {
      id: CLIENT_NAV_SECTION_ID,
      items: CLIENT_MENU_ITEM_DEFINITIONS.filter((item) => {
        if (item.featureFlag && !featureFlags[item.featureFlag]) {
          return false;
        }
        if (item.hideWhenFeatureFlag && featureFlags[item.hideWhenFeatureFlag]) {
          return false;
        }
        // If no client is selected, only show Admin workspace.
        if (!clientId && item.id !== CLIENT_MENU_IDS.adminWorkspace) {
          return false;
        }
        return true;
      }).map(function mapItem({
        getTo,
        getActions,
        children,
        featureFlag: _featureFlag,
        hideWhenFeatureFlag: _hideWhenFeatureFlag,
        ...item
      }): SidebarMenuItem {
        return {
          ...item,
          to: getTo?.(effectiveClientPaths),
          actions: getActions?.(effectiveClientPaths),
          children: children
            ?.filter((child) => {
              if (child.featureFlag && !featureFlags[child.featureFlag]) {
                return false;
              }
              if (child.hideWhenFeatureFlag && featureFlags[child.hideWhenFeatureFlag]) {
                return false;
              }
              return true;
            })
            .map(mapItem),
        };
      }),
    },
  ];

  const activeItemId = effectiveClientPaths
    ? getActiveClientMenuItemId(pathname, effectiveClientPaths, isProgramTemplate, locationState?.fromSection)
    : undefined;

  const onWorkspaceSelect = (workspaceId: string) => {
    if (workspaceId === ALL_CLIENTS_WORKSPACE_ID) {
      requestNavigation({ run: () => navigate(AppRoute.clients) });
      return;
    }

    const currentSection = getClientSectionFromPathname(pathname) || AppRoute.recentCreatives;
    const nextPath = buildPath(AppRoute.clients, workspaceId, currentSection);
    requestNavigation({ run: () => navigate(nextPath) });
  };

  return (
    <>
      <SidebarMenu.WorkspaceSwitcher
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onWorkspaceSelect={onWorkspaceSelect}
        placeholder="All clients"
      />
      <SidebarMenu.Items sections={sections} activeItemId={activeItemId} />
    </>
  );
};
